/* Divine Medicinals — Cart follows the customer
   ------------------------------------------------------------------
   Shopify's cart lives in a cookie, so it belongs to one browser and nothing
   else. A shopper who fills a bag on their phone and then opens the laptop
   finds it empty. This carries the lines themselves (never the cart token,
   which is bound to one session and cannot be handed over) through the same
   App Proxy the wish list uses.

     GET  <proxy>/cart  -> { customer, cart }
     POST <proxy>/cart  -> { customer, cart }   body { cart, mode }

   Who is signed in is decided by the server from Shopify's signature, exactly
   as in wishlist-sync.js. Signed-out shoppers, a missing proxy, an unreachable
   backend or a blocked localStorage all fall back to the ordinary per-browser
   cart, with nothing shown to the shopper.

   Rules, in the order they matter:
     - on a browser that has never synced, a guest bag is UNIONED in, so
       signing in never loses what you just put in the cart
     - on a browser where somebody ELSE last synced, the account's own cart
       wins outright. Shopify does not clear its cart cookie between customers,
       so a shared laptop would otherwise write one shopper's bag onto another
       shopper's customer record
     - after that the newest write wins, so emptying the bag on one device
       empties it on the next (a union could never express a removal)
     - a line that has since sold out is dropped and the corrected cart pushed
       back, rather than blocking the whole restore
     - if a restore cannot be completed, this browser's cart is put back the
       way it was rather than left empty

   The one rule the code must never break: a cart we FAILED to read is not an
   empty cart. Every read that comes back unusable aborts the write it was
   feeding, because pushing "[]" as the newest state would delete the shopper's
   bag on every device they own.

   Every "u" stamp comes from the Worker, never from a device clock, so a phone
   set to the wrong time cannot win every conflict for ever.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var CFG = window.DMWishlistConfig || {};
  var PROXY = (CFG.proxyBase || '').replace(/\/+$/, '');
  if (!PROXY) return;                       // backend not set up — plain local cart
  if (CFG.cartSync === false) return;       // switched off in theme settings

  var ROOT = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, '');
  var META_KEY = 'dm_cart_sync_v1';
  var PUSH_DELAY = 1200;
  var MAX_BACKOFF = 60000;

  // Shopify can report a signed-in shopper as signed OUT on a page load — the
  // storefront customer session is not always attached by the time the first
  // proxied request goes out under new customer accounts. Latching "guest" on
  // that first answer means everything saved on that page is silently dropped,
  // so identity is re-checked instead of being decided once.
  var IDENTITY_RETRIES = [2000, 5000, 12000, 30000, 60000];
  var identityTries = 0, identityTimer = null;

  var pushTimer = null, inFlight = false, dirty = false, backoff = 1000;
  var forcePush = false;                    // send even if it looks unchanged
  var applying = false;                     // true while WE rewrite the cart
  var customerId = null;

  /* ---------------- small helpers ---------------- */

  function setStatus(s, detail) {
    document.documentElement.setAttribute('data-cart-sync', s);
    document.dispatchEvent(new CustomEvent('dm:cartsync', {
      detail: Object.assign({ status: s }, detail || {})
    }));
  }

  function readMeta() {
    try { return JSON.parse(localStorage.getItem(META_KEY) || 'null'); } catch (e) { return null; }
  }

  /**
   * Record what this browser believes the server holds.
   *
   * A pending flag means the opposite: the local cart is NOT what the server
   * has, and a correction still has to go out. It is persisted because the
   * retry flag itself lives only in memory, and a shopper who navigates away
   * in the wrong second would otherwise strand their cart for good.
   */
  function remember(u, s, pending) {
    try {
      localStorage.setItem(META_KEY, JSON.stringify({
        customer: customerId, u: u || 0, sig: s, pending: !!pending
      }));
    } catch (e) {}
  }

  function proxy(path, opts) {
    return fetch(PROXY + path, Object.assign({
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    }, opts || {})).then(function (r) {
      if (!r.ok) { var e = new Error('cart sync http ' + r.status); e.status = r.status; throw e; }
      return r.json();
    });
  }

  function shop(path, opts) {
    return fetch(ROOT + path, Object.assign({
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    }, opts || {}));
  }

  /* ---------------- the cart as a document ---------------- */

  /** Only the properties a shopper actually set; Shopify hands back {} for none. */
  function props(p) {
    if (!p || typeof p !== 'object') return null;
    var out = {}, keys = Object.keys(p).sort(), n = 0;
    for (var i = 0; i < keys.length; i++) {
      var v = p[keys[i]];
      if (v === null || v === undefined || typeof v === 'object') continue;
      if (keys[i] === '__proto__' || keys[i] === 'constructor' || keys[i] === 'prototype') continue;
      out[keys[i]] = String(v);
      n++;
    }
    return n ? out : null;
  }

  /** Shopify's own /cart.js shape -> the compact document we sync. */
  function toDoc(cart) {
    var items = ((cart && cart.items) || []).map(function (it) {
      var sp = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan;
      return {
        id: String(it.variant_id),
        q: it.quantity,
        p: props(it.properties),
        sp: sp ? String(sp.id) : null
      };
    });
    return {
      v: 1,
      items: items,
      note: (cart && cart.note) || null,
      attrs: props(cart && cart.attributes)
    };
  }

  function lineSig(it) {
    return [it.id, it.sp == null ? '' : it.sp, it.p ? JSON.stringify(it.p) : ''].join('|');
  }

  /** Same fingerprint the Worker uses, so both ends agree on "unchanged". */
  function sig(doc) {
    if (!doc) return '';
    var lines = (doc.items || []).map(function (it) { return lineSig(it) + '#' + it.q; }).sort().join(';');
    return lines + '||' + (doc.note || '') + '||' + (doc.attrs ? JSON.stringify(doc.attrs) : '');
  }

  var EMPTY = { v: 1, items: [], note: null, attrs: null };

  /**
   * Read the live cart. Resolves with Shopify's cart object, or with null when
   * the read FAILED — which is not the same thing as an empty cart, and every
   * caller has to tell them apart before writing anything anywhere.
   */
  function getCart() {
    return shop('/cart.js', { method: 'GET' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { return (c && Array.isArray(c.items)) ? c : null; })
      .catch(function () { return null; });
  }

  /* ---------------- writing a document back into the cart ---------------- */

  function addLines(items) {
    if (!items.length) return Promise.resolve({ added: [], failed: [] });

    var payload = items.map(function (it) {
      var line = { id: Number(it.id), quantity: it.q };
      if (it.p) line.properties = it.p;
      if (it.sp) line.selling_plan = Number(it.sp);
      return line;
    });

    // one round trip when everything is still buyable, which is the norm
    return shop('/cart/add.js', { method: 'POST', body: JSON.stringify({ items: payload }) })
      .then(function (r) {
        if (r.ok) return { added: items, failed: [] };

        // /cart/add.js is all-or-nothing, so on any rejection fall back to
        // adding line by line and keep whatever the shop still sells
        var added = [], failed = [];
        return payload.reduce(function (chain, line, i) {
          return chain.then(function () {
            return shop('/cart/add.js', { method: 'POST', body: JSON.stringify({ items: [line] }) })
              .then(function (rr) { (rr.ok ? added : failed).push(items[i]); })
              .catch(function () { failed.push(items[i]); });
          });
        }, Promise.resolve()).then(function () { return { added: added, failed: failed }; });
      });
  }

  /**
   * Bring the note and cart attributes into line with the document. Anything
   * the shopper cleared on another device has to be cleared HERE too, so both
   * are always sent, with an empty string where a value has gone away.
   */
  function applyNoteAndAttrs(doc, previous) {
    var upd = { note: doc.note || '' };
    var want = doc.attrs || {};
    var had = (previous && previous.attrs) || {};
    var keys = Object.keys(want).concat(Object.keys(had)).filter(function (k, i, a) { return a.indexOf(k) === i; });
    if (keys.length) {
      var attrs = {};
      keys.forEach(function (k) { attrs[k] = Object.prototype.hasOwnProperty.call(want, k) ? want[k] : ''; });
      upd.attributes = attrs;
    }
    if (!upd.note && !upd.attributes && !(previous && previous.note)) return Promise.resolve();
    return shop('/cart/update.js', { method: 'POST', body: JSON.stringify(upd) }).catch(function () {});
  }

  /**
   * Rebuild this browser's cart to match the document. Clearing first is what
   * makes a removal on another device actually take effect here.
   *
   * Resolves with the cart as it really ended up, read back from Shopify: lines
   * that have sold out are gone, and Shopify may have stacked or capped others.
   * If the restore fails outright, the previous cart is put back first.
   */
  function applyDoc(doc, previous) {
    applying = true;

    return shop('/cart/clear.js', { method: 'POST', body: '{}' })
      .then(function (r) {
        // shop() hands back the raw response, so an unchecked clear makes a
        // throttled or failed request look identical to a successful one. That
        // is how a browser ends up believing it emptied a cart it did not.
        if (!r || !r.ok) throw new Error('cart clear failed (' + (r && r.status) + ')');
        return addLines(doc.items || []);
      })
      .then(function (res) {
        // a total wipe-out: nothing we wanted went in, but the shopper had a
        // cart a moment ago. Put it back rather than leaving them with nothing.
        if (!res.added.length && (doc.items || []).length && previous && previous.items.length) {
          return addLines(previous.items).then(function () {
            throw new Error('restore failed, previous cart put back');
          });
        }
        return applyNoteAndAttrs(doc, previous);
      })
      .then(function () { return getCart(); })
      .then(function (live) {
        // a failed read-back must not be reported as an empty cart
        var actual = live ? toDoc(live) : null;
        release();
        return actual;
      })
      .catch(function (e) {
        release();
        throw e;
      });

    function release() {
      // let the event out while still guarded, so our own rewrite is not
      // mistaken for a shopper action and pushed straight back
      document.dispatchEvent(new CustomEvent('dm:cart:updated'));
      setTimeout(function () { applying = false; }, 0);
    }
  }

  /* ---------------- reconcile on load ---------------- */

  function start() {
    setStatus('checking');

    Promise.all([proxy('/cart'), getCart()]).then(function (r) {
      var res = r[0], liveCart = r[1];
      customerId = res && res.customer ? String(res.customer) : null;

      if (!customerId) {                    // signed out, or not recognised YET
        setStatus('guest');
        recheckIdentity();
        return;
      }
      identityTries = 0;
      clearTimeout(identityTimer);

      var meta = readMeta();
      var server = res.cart;

      // The cart read failed. Do nothing at all rather than guess: no union, no
      // adopt, no remembering a state we never saw.
      if (!liveCart) { setStatus('offline', { error: 'cart unreadable' }); return; }

      var local = toDoc(liveCart);
      var localSig = sig(local);

      // A browser that has never synced anyone's cart: whatever is in it was
      // put there by this shopper as a guest, so fold it in.
      if (!meta) {
        if (local.items.length) {
          return proxy('/cart', { method: 'POST', body: JSON.stringify({ cart: local, mode: 'merge' }) })
            .then(function (out) { return adopt(out.cart, local, 'merged'); });
        }
        if (server && server.items.length) return adopt(server, local, 'restored');
        remember(server ? server.u : 0, localSig);
        setStatus('synced', { customer: customerId });
        return;
      }

      // Somebody else synced last on this browser. Shopify keeps its cart
      // cookie across a sign-out, so those lines are very likely theirs, not
      // this shopper's: take the account's own cart and let the restore clear
      // the leftovers. Never union — that would write one customer's bag onto
      // another customer's record.
      if (meta.customer !== customerId) {
        return adopt(server || EMPTY, local, 'restored');
      }

      // A correction was owed from a previous visit and never got out.
      if (meta.pending) { forcePush = true; schedulePush(0); setStatus('synced'); return; }

      // Same shopper, same browser. Did this cart change while we were away?
      if (localSig !== meta.sig) { schedulePush(0); setStatus('synced'); return; }

      // Otherwise take the server's copy if it has moved on since our last sync.
      if (server && (server.u || 0) > (meta.u || 0) && sig(server) !== localSig) {
        return adopt(server, local, 'restored');
      }

      remember(meta.u, localSig);
      setStatus('synced', { customer: customerId });
    }).catch(function (e) {
      if (e && e.status === 401) { customerId = null; setStatus('guest'); return; }
      setStatus('offline', { error: String((e && e.message) || e) });
    });
  }

  /** Ask again whether anyone is signed in, backing off, a handful of times. */
  function recheckIdentity() {
    if (identityTries >= IDENTITY_RETRIES.length) return;
    var wait = IDENTITY_RETRIES[identityTries++];
    clearTimeout(identityTimer);
    identityTimer = setTimeout(start, wait);
  }

  function adopt(doc, previous, why) {
    if (!doc) return;
    return applyDoc(doc, previous).then(function (actual) {
      if (!actual) {                        // could not read the cart back
        setStatus('offline', { error: 'cart unreadable after restore' });
        return;
      }
      var actualSig = sig(actual);
      var adjusted = actualSig !== sig(doc);
      // When the cart did NOT end up matching what we adopted, the server's
      // stamp must not be recorded against it: that pair claims the server
      // holds this cart, which is false, and every later load would then see
      // nothing to do. u:0 keeps the server looking newer so the next load
      // retries, and the pending flag carries the correction across a reload.
      remember(adjusted ? 0 : (doc.u || 0), actualSig, adjusted);
      setStatus('synced', { customer: customerId, action: why, adjusted: adjusted });
      // Lines were dropped (sold out, throttled, capped), so the server still
      // offers something this shopper cannot have. Force the correction through:
      // an ordinary push would compare equal to what we just remembered and do
      // nothing, leaving every device to repeat the same failed restore.
      if (adjusted) { forcePush = true; schedulePush(0); }
    }).catch(function (e) {
      setStatus('offline', { error: String((e && e.message) || e) });
    });
  }

  /* ---------------- push on change ---------------- */

  function schedulePush(delay) {
    if (!customerId) return;
    dirty = true;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(push, delay === undefined ? PUSH_DELAY : delay);
  }

  function push() {
    if (!customerId) return;
    // mid-restore the cart is momentarily empty; uploading that would delete
    // the shopper's bag everywhere
    if (applying) { schedulePush(300); return; }
    if (inFlight) { dirty = true; return; }

    inFlight = true;
    setStatus('saving');

    getCart().then(function (live) {
      if (!live) {
        // failed read: keep the pending edit and try again, never send "[]"
        inFlight = false;
        setStatus('offline', { error: 'cart unreadable' });
        backoff = Math.min(MAX_BACKOFF, backoff * 2);
        schedulePush(backoff);
        return;
      }

      var doc = toDoc(live);
      var s = sig(doc);
      var meta = readMeta();
      if (!forcePush && meta && meta.customer === customerId && meta.sig === s) {
        inFlight = false;
        dirty = false;
        setStatus('synced');
        return;
      }

      return proxy('/cart', { method: 'POST', body: JSON.stringify({ cart: doc }) })
        .then(function (out) {
          inFlight = false;
          dirty = false;
          forcePush = false;
          backoff = 1000;

          if (!out || !out.customer) {      // session ended between calls
            customerId = null;
            setStatus('guest');
            return;
          }
          var saved = out.cart;
          // the server may hand back a newer cart saved on another device
          if (saved && sig(saved) !== s) return adopt(saved, doc, 'restored');
          remember(saved ? saved.u : 0, s);
          setStatus('synced');
          if (dirty) schedulePush();
        });
    }).catch(function (e) {
      inFlight = false;
      if (e && e.status === 401) { customerId = null; setStatus('guest'); return; }
      setStatus('offline', { error: String((e && e.message) || e) });
      backoff = Math.min(MAX_BACKOFF, backoff * 2);
      schedulePush(backoff);
    });
  }

  /* ---------------- noticing that the cart changed ---------------- */

  document.addEventListener('dm:cart:updated', function () {
    if (applying) return;                   // our own rewrite, do not echo it back
    // A change made while we think nobody is signed in is exactly when a stale
    // "guest" verdict costs the shopper their basket. Ask again right now; if a
    // customer turns up, start() folds this cart into their account.
    if (!customerId) { identityTries = 0; start(); return; }
    schedulePush();
  });

  // The cart page posts a form and reloads, and other theme code may call the
  // cart endpoints without announcing it. Watch the endpoints themselves so
  // nothing has to remember to fire the event.
  if (window.fetch) {
    var native = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : ((input && input.url) || '');
      var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var writes = method === 'POST' && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
      return native.apply(this, arguments).then(function (res) {
        if (writes && res.ok && !applying) schedulePush();
        return res;
      });
    };
  }

  window.addEventListener('pageshow', function (e) { if (e.persisted) { identityTries = 0; start(); } });
  // coming back to the tab is a good moment to notice a session that has since resolved
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && !customerId) { identityTries = 0; start(); }
  });
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush();
  });

  /** Last chance to save an edit when the tab goes away. */
  function flush() {
    if (!dirty || !customerId || inFlight || applying) return;
    clearTimeout(pushTimer);
    getCart().then(function (live) {
      if (!live) return;                    // failed read: leave it dirty, never send "[]"
      dirty = false;
      var body = JSON.stringify({ cart: toDoc(live) });
      if (navigator.sendBeacon) {
        try {
          navigator.sendBeacon(PROXY + '/cart', new Blob([body], { type: 'application/json' }));
          return;
        } catch (e) { /* fall through */ }
      }
      fetch(PROXY + '/cart', {
        method: 'POST', body: body, keepalive: true,
        credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }
      }).catch(function () {});
    }).catch(function () {});
  }

  window.DMCartSync = {
    push: function () { schedulePush(0); },
    refresh: start,
    status: function () { return document.documentElement.getAttribute('data-cart-sync'); }
  };

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
